"use client";

import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import { isNativePlatform } from "@/lib/capacitor/platform";
import { useState, useEffect } from "react";
import { useRouter } from "nextjs-toploader/app";
import { X, Check, CreditCard, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Badge } from "@/app/_module/components/ui/badge";
import { Dialog, ResponsiveDialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/app/_module/components/ui/dialog";
import { routes } from "@/app/_module/constants/routes";
import { useGetSubscriptionPlans } from "@/app/(authenticated)/settings/_modules/hooks/queries";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { useToast } from "@/app/_module/hooks/use-toast";

interface Plan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  features: string[];
  popular?: boolean;
}

interface SubscriptionRenewalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: (planId: string) => void;
}

export default function SubscriptionRenewalModal({ 
  isOpen, 
  onClose, 
  onUpgrade 
}: SubscriptionRenewalModalProps) {
  const router = useRouter();
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch plans from backend
  const { data: plansData, isLoading: plansLoading, error: plansError } = useGetSubscriptionPlans({
    page: 1,
    limit: 10
  });

  const handleUpgrade = async () => {
    if (!selectedPlan) return;
    
    setIsLoading(true);
    try {
      // Real API call to upgrade subscription
      const response = await api.post({
        url: getApiUrl("/subscriptions/upgrade"),
        body: {
          planId: selectedPlan
        }
      });
      
      if (response.data && (response.data as any).statusCode === 200) {
        onUpgrade(selectedPlan);
        onClose();
      } else {
        throw new Error('Failed to upgrade subscription');
      }
    } catch (error) {

      toast({
        title: "Upgrade Failed",
        description: "Failed to upgrade subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageSubscription = () => {
    if (!allowCheckout("subscription")) return;
    router.push(routes.settings.path + "?tab=subscription");
    onClose();
  };

  // Native app: NEVER show plan cards/prices or run the in-app upgrade charge
  // (Apple 3.1.1 / Play payments) — instruction-only card, same as the
  // subscription settings tab.
  if (isNativePlatform()) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <ResponsiveDialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Account Renewal</DialogTitle>
            <DialogDescription>
              Plan and account management is available on the web version of
              InsuraCentral. To renew or change your plan, log in on your
              computer at app.insuracentral.com.
            </DialogDescription>
          </DialogHeader>
        </ResponsiveDialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <ResponsiveDialogContent size="3xl" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <DialogTitle className="text-xl font-semibold text-gray-900">
                  Subscription Expired
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Your trial has ended. Choose a plan to continue using all features.
                </DialogDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Status */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-3" />
              <div>
                <h3 className="font-medium text-red-800">Account in Full Lockdown Mode</h3>
                <p className="text-sm text-red-600 mt-1">
                  All features are locked including dialing, lead management, CRM, and analytics. Upgrade to restore full access.
                </p>
              </div>
            </div>
          </div>

          {/* Plan Selection */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Your Plan</h3>
            
            {plansLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-600">Loading plans...</span>
              </div>
            ) : plansError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800">Failed to load subscription plans. Please try again later.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {(() => {
                  // Handle both response formats - array or object with items
                  const plans = Array.isArray(plansData) ? plansData : plansData?.items || [];
                  return plans.map((plan: any) => (
                    <Card 
                      key={plan.id}
                      className={`relative cursor-pointer transition-all duration-200 ${
                        selectedPlan === plan.id 
                          ? 'ring-2 ring-blue-500 border-blue-500' 
                          : 'hover:shadow-md'
                      }`}
                      onClick={() => setSelectedPlan(plan.id)}
                    >
                      {plan.name.toLowerCase().includes('full_fledged') || plan.name.toLowerCase().includes('premium') && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <Badge className="bg-blue-500 text-white px-4 py-1">
                            Most Popular
                          </Badge>
                        </div>
                      )}
                      
                      <CardHeader className="text-center pb-4">
                        <CardTitle className="text-xl">{plan.name}</CardTitle>
                        <div className="text-3xl font-bold text-gray-900">
                          ${plan.price}
                          <span className="text-lg font-normal text-gray-500">/month</span>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="space-y-4">
                        <ul className="space-y-2">
                          {plan.features?.map((feature: string, index: number) => (
                            <li key={index} className="flex items-center text-sm text-gray-600">
                              <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                              {feature}
                            </li>
                          )) || (
                            <li className="text-sm text-gray-500">No features listed</li>
                          )}
                        </ul>
                        
                        <Button
                          className={`w-full ${
                            selectedPlan === plan.id
                              ? 'bg-blue-600 hover:bg-blue-700'
                              : 'bg-gray-600 hover:bg-gray-700'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPlan(plan.id);
                          }}
                        >
                          {selectedPlan === plan.id ? 'Selected' : 'Select Plan'}
                        </Button>
                      </CardContent>
                    </Card>
                  ));
                })()}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
            <Button
              onClick={handleUpgrade}
              disabled={!selectedPlan || isLoading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              size="lg"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                <div className="flex items-center">
                  <CreditCard className="h-5 w-5 mr-2" />
                  Upgrade Now
                </div>
              )}
            </Button>
            
            <Button
              onClick={handleManageSubscription}
              variant="outline"
              className="flex-1"
              size="lg"
            >
              Manage Subscription
            </Button>
          </div>

          {/* Additional Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Need Help?</h4>
            <p className="text-sm text-gray-600">
              If you're an agency owner paying for your agents, or if you have questions about your subscription, 
              please contact support or check your subscription settings.
            </p>
          </div>
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
}
